import { ICheckinState, useReportState } from './state';
import { GetState, SetState } from 'zustand';
import { ProfileData } from '../model/Model';
import { checkIn, checkOut, getWorker, searchWorker } from '../service/rest';
import { isCheckedIn } from '../service/util';
import { getProfile, saveProfile } from '../service/storage';
import { CheckinForm } from '../service/checkin-flow';

export enum SearchVisitorState {
    SEARCH,
    NO_WORKER_FOUND,
    NO_ACTIVE_LOGIN,
    FOUND
}

export const searchFunc = (set: SetState<ICheckinState>) =>
    async (token: string, profile: ProfileData, rememberMe: boolean, callback: (state: SearchVisitorState) => void) => {
        try {
            set({
                loaded: false,
                loading: true,
                activeCheckin: undefined,
                lastCheckin: undefined,
            });
            const worker = await searchWorker(token, profile);
            if (worker) {
                if (isCheckedIn(worker.lastSiteLogin, worker.lastSiteLogout)) {
                    set({
                        loaded: true,
                        activeCheckin: {
                            workerId: worker.id,
                            checkinTime: worker.lastSiteLogin,
                            profile: {
                                ...profile,
                                rememberMe: rememberMe,
                            },
                        },
                    });
                    callback(SearchVisitorState.FOUND);
                } else {
                    set({
                        loaded: true,
                        lastCheckin: {
                            workerId: worker.id,
                            checkinTime: worker.lastSiteLogin,
                            checkoutTime: worker.lastSiteLogout,
                            profile: {
                                ...profile,
                                rememberMe: rememberMe,
                            },
                        },
                    });
                    callback(SearchVisitorState.NO_ACTIVE_LOGIN);
                }
                saveProfile(token, {
                    workerId: worker.id,
                    profile: profile,
                    checkinTime: worker.lastSiteLogin,
                    checkoutTime: worker.lastSiteLogout
                });
            } else {
                set({
                    loaded: true,
                });
                callback(SearchVisitorState.NO_WORKER_FOUND);
            }
        } catch (e) {
            const { getState } = useReportState;
            getState().reportError('Network error while fetching searching for worker, please try again later');
        } finally {
            set({
                loading: false,
            });
        }
    };

export const checkInFunc = (set: SetState<ICheckinState>) =>
    async (token: string, checkinForm: CheckinForm, callback: (success: boolean) => void) => {
        try {
            set({
                loaded: false,
                loading: true,
            });
            const workerResult= await checkIn(token, checkinForm);
            if (workerResult) {
                if (workerResult.success) {
                    const now = new Date().toString();
                    set({
                        loaded: true,
                        activeCheckin: {
                            profile: checkinForm,
                            checkinTime: now,
                            workerId: workerResult.worker.id,
                        },
                    });
                    saveProfile(token, {
                        profile: checkinForm,
                        checkinTime: now,
                        workerId: workerResult.worker.id,
                    });
                    callback(true);
                } else {
                    set({
                        loaded: true,
                        activeCheckin: undefined,
                    });
                    callback(false);
                }
            } else {
                const { getState } = useReportState;
                getState().reportError('Network error while checking in, please try again later');
            }
        } catch (e) {
            const { getState } = useReportState;
            getState().reportError('Network error while checking in, please try again later');
        } finally {
            set({
                loading: false,
            });
        }
    };

export const checkoutFunc = (set: SetState<ICheckinState>, get: GetState<ICheckinState>) =>
    async (token: string) => {
        try {
            set({
                loaded: false,
                loading: true,
            });
            const active = get().activeCheckin;
            const workerId = active?.workerId;
            if (workerId) {
                const worker = await checkOut(token, workerId);
                set({
                    loaded: true,
                    activeCheckin: undefined,
                    lastCheckin: {
                        ...active,
                        checkinTime: worker?.lastSiteLogin,
                        checkoutTime: worker?.lastSiteLogout,
                    },
                });
            }
        } catch (e) {
            const { getState } = useReportState;
            getState().reportError('Network error while checking out, please try again later');
        } finally {
            set({
                loading: false,
            });
        }
    };

export const fetchFunc = (set: SetState<ICheckinState>) =>
    async (token: string) => {
        try {
            set({
                loaded: false,
                loading: true,
                activeCheckin: undefined,
                lastCheckin: undefined
            });
            const localProfile = getProfile(token);
            if (localProfile) {
                const { workerId, profile } = localProfile;
                if (workerId) {
                    const worker = await getWorker(token, workerId);
                    if (worker) {
                        if (isCheckedIn(worker.lastSiteLogin, worker.lastSiteLogout)) {
                            set({
                                loaded: true,
                                activeCheckin: {
                                    workerId: worker.id,
                                    checkinTime: worker.lastSiteLogin,
                                    profile: {
                                        ...profile,
                                        rememberMe: true,
                                    },
                                },
                            });
                        } else {
                            set({
                                loaded: true,
                                lastCheckin: {
                                    workerId: worker.id,
                                    checkinTime: worker.lastSiteLogin,
                                    checkoutTime: worker.lastSiteLogout,
                                    profile: {
                                        ...profile,
                                        rememberMe: true,
                                    },
                                },
                            });
                        }
                    }
                } else {
                    // No locale state
                    set({
                        loaded: true,
                        lastCheckin: {
                            ...localProfile
                        }
                    });
                }
            } else {
                set({
                    loaded: true
                })
            }
        } catch (e) {
            const { getState } = useReportState;
            getState().reportError('Network error while fetching checkin state, please try again later');
        } finally {
            set({
                loading: false,
            });
        }
    };
